import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { employeeIncidentBulkRejectService } from '@/api/domains/employeeIncident';
import type { LC_BulkRejectIncidentsOut } from '@/api/domains/employeeIncident/types';
import { useUI } from '@/hooks/ui/useUI';
import type { IncidentBulkRejectionModalFilters } from './useIncidentBulkRejectionModalList';


interface UseIncidentBulkRejectionParams {
  selectedIncidentIds: number[];
  onSuccess: (result: LC_BulkRejectIncidentsOut) => void;
  /**
   * Modo "seleccionar todo" (ASIS-5250): cuando es `true`, se ignora `selectedIncidentIds` y
   * se envían los filtros vigentes + los IDs excluidos manualmente — el backend resuelve el
   * conjunto real de incidencias Pendientes a rechazar al momento de ejecutar.
   */
  isSelectAllMode?: boolean;
  filters?: IncidentBulkRejectionModalFilters;
}

export interface UseIncidentBulkRejectionResult {
  comment: string;
  setComment: (value: string) => void;
  canSubmit: boolean;
  isSubmitting: boolean;
  handleSubmit: () => Promise<void>;
}

/**
 * Lógica para el rechazo masivo de incidentes de marcaje (ASIS-5250).
 * En modo lista explícita, envía los IDs seleccionados como CSV. En modo "seleccionar todo",
 * envía los filtros vigentes (dateFrom/dateTo/errorTypeId/filterEmployeeId/searchText) más los
 * IDs excluidos manualmente, y el backend resuelve el conjunto real al momento de ejecutar.
 * Endpoint: PUT /v1/attendances/incidents/bulk-rejection.
 */
export function useIncidentBulkRejection({
  selectedIncidentIds,
  onSuccess,
  isSelectAllMode = false,
  filters,
}: UseIncidentBulkRejectionParams): UseIncidentBulkRejectionResult {
  const { t: translate } = useTranslation();
  const { showApiError, showSuccess, showWarning } = useUI();

  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const canSubmit = comment.trim().length > 0 && !isSubmitting;

  const handleSubmit = useCallback(async () => {
    if (submittingRef.current || comment.trim().length === 0) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      const response = await employeeIncidentBulkRejectService.bulkReject(
        isSelectAllMode
          ? {
            rejectAll: true,
            approverComment: comment.trim(),
            dateFrom: filters?.dateFrom ?? null,
            dateTo: filters?.dateTo ?? null,
            errorTypeId: filters?.errorTypeId ?? null,
            filterEmployeeId: filters?.filterEmployeeId ?? null,
            searchText: filters?.searchText || null,
            excludeIncidentIds: filters?.excludeIncidentIds?.length
              ? filters.excludeIncidentIds.join(',')
              : undefined,
          }
          : {
            incidentIds: selectedIncidentIds.join(','),
            approverComment: comment.trim(),
          },
      );

      if (!response.success || !response.data) {
        showApiError(response, translate('attendance:incidentManagement.messages.rejectError'));
        throw new Error('bulk-reject-incidents-failed');
      }

      const result = response.data;

      // Caso no-op (ASIS-5250, modo "seleccionar todo"): el conjunto resuelto por el backend
      // quedó vacío — ej. otro usuario ya resolvió todo lo pendiente entre la activación de la
      // selección y la confirmación, o se excluyó todo manualmente. totalAlreadyResolved === 0
      // por sí solo no distingue este caso de un éxito real — hay que mirar también totalRejected.
      if (result.totalRejected === 0 && result.totalAlreadyResolved === 0) {
        showWarning(translate('attendance:incidentManagement.bulk.nothingToReject'));
      } else if (result.totalAlreadyResolved === 0) {
        showSuccess(translate('attendance:incidentManagement.bulk.rejectSuccess'));
      } else {
        showWarning(
          translate('attendance:incidentManagement.bulk.rejectPartialResult', {
            processed: result.totalRejected,
            skipped: result.totalAlreadyResolved,
          }),
        );
      }

      onSuccess(result);
      setComment('');
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [
    comment,
    selectedIncidentIds,
    isSelectAllMode,
    filters,
    showApiError,
    showSuccess,
    showWarning,
    translate,
    onSuccess,
  ]);

  return {
    comment,
    setComment,
    canSubmit,
    isSubmitting,
    handleSubmit,
  };
}

export default useIncidentBulkRejection;
